import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useServerSentEvents } from '@dealstack/frontend/shared/hooks/useServerSentEvents';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import {
	buildInvestmentProgrammeDocumentQueryKey,
	buildInvestmentProgrammeDocumentsListKeyPrefix,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import type {
	ProgrammeDocumentsStreamMessage,
	UseProgrammeDocumentsStreamArgs,
	UseProgrammeDocumentsStreamResult,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useProgrammeDocumentsStream.types';

export const useProgrammeDocumentsStream = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	enabled = true,
}: UseProgrammeDocumentsStreamArgs): UseProgrammeDocumentsStreamResult => {
	const queryClient = useQueryClient();

	/** The list shows every document in the programme, so any change moves it. */
	const invalidateList = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: buildInvestmentProgrammeDocumentsListKeyPrefix({ dealId, programmeId }),
		});
	}, [queryClient, dealId, programmeId]);

	/** The detail and activity queries only move for the document on screen. */
	const invalidateOpenDocument = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: buildInvestmentProgrammeDocumentQueryKey({
				dealId,
				programmeId,
				documentWorkflowInstanceId,
			}),
		});
		void queryClient.invalidateQueries({
			queryKey: getDocumentWorkflowEventsQueryKey({
				programmeId,
				documentWorkflowInstanceId,
			}),
		});
	}, [queryClient, dealId, programmeId, documentWorkflowInstanceId]);

	const invalidateAll = useCallback(() => {
		invalidateOpenDocument();
		invalidateList();
	}, [invalidateOpenDocument, invalidateList]);

	const onMessage = useCallback(
		(message: ProgrammeDocumentsStreamMessage) => {
			if (message.type !== 'changed') {
				return;
			}

			logger.debug(
				`Programme-documents SSE 'changed' received for programme ${programmeId}; invalidating queries`
			);

			invalidateList();

			if (
				message.documentWorkflowInstanceId === null ||
				message.documentWorkflowInstanceId === documentWorkflowInstanceId
			) {
				invalidateOpenDocument();
			}
		},
		[invalidateList, invalidateOpenDocument, documentWorkflowInstanceId, programmeId]
	);

	const sse = useServerSentEvents<ProgrammeDocumentsStreamMessage>({
		path: formatUnicorn(API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_STREAM_GET, {
			dealId,
			programmeId,
		}),
		enabled: Boolean(enabled && dealId && programmeId),
		onMessage,
		onResync: invalidateAll,
		logLabel: `Programme-documents (programme ${programmeId})`,
	});

	return { ...sse, resync: invalidateAll };
};
